import { useLayoutEffect, useRef, useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Bordered, always-open card for the account page Overview "Grid" view (v1.5.75).
 *
 * The body is a fixed height (`capPx`, default 320) with an inner scrollbar, so
 * every grid card is the same size whether empty or full; the card fills its grid
 * cell (`h-full` + `flex-1` body) so a row-mate with a footer can't make it shorter.
 * It also has a
 * "Show more / Show less" footer that lifts the cap. The footer only renders when
 * the content actually overflows the cap, measured on the inner content wrapper
 * (not the scroll box) so the measurement is independent of the expanded state
 * and survives async query loads via ResizeObserver. `capPx={null}` = no cap and
 * no footer (the Contacts card, which grows with its list by design).
 *
 * `expandKey`: when it becomes truthy the card expands itself — the Projects card
 * passes the `?openProject=` id so a deep-linked row's opened task list is never
 * hidden behind the cap (ProjectRow's scrollIntoView scrolls nested scroll boxes,
 * which would otherwise land the row at the top of a 320px window).
 */
export function OverviewCard({
  title,
  count,
  action,
  capPx = 320,
  expandKey,
  children,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  capPx?: number | null;
  expandKey?: string | null;
  children: React.ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || capPx == null) return;
    const measure = () => setOverflows(el.offsetHeight > capPx + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [capPx]);

  useEffect(() => {
    if (expandKey) setExpanded(true);
  }, [expandKey]);

  const capped = capPx != null && !expanded;

  return (
    <div className="rounded-lg border bg-card shadow-sm min-w-0 h-full flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold text-foreground tracking-tight truncate">{title}</span>
          {count !== undefined && (
            <span className="text-xs text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 font-medium leading-none shrink-0">
              {count}
            </span>
          )}
        </span>
        {action && <span className="shrink-0">{action}</span>}
      </div>
      <div
        className="px-4 pt-3 pb-3 overflow-x-hidden flex-1"
        style={capped ? { height: capPx, overflowY: "auto" } : undefined}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      {capPx != null && (overflows || expanded) && (
        <button
          type="button"
          className="w-full flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground py-1.5 border-t transition-colors"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <>Show less <ChevronUp className="w-3.5 h-3.5" /></>
          ) : (
            <>Show more <ChevronDown className="w-3.5 h-3.5" /></>
          )}
        </button>
      )}
    </div>
  );
}
